import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'

interface TierPermissions {
  can_read: boolean
  can_write: boolean
  can_delete: boolean
  max_customers: number  // -1 = unlimited, 0 = none
  max_deals: number
  max_tasks: number
}

interface MembershipTier {
  id: number
  name: string
  price: number
  stripe_price_id?: string | null
  permissions: TierPermissions | string
  is_active: boolean
  is_trial: boolean
  position: number
  user_count?: number
  created_at: string
  updated_at: string
}

const DEFAULT_PERMISSIONS: TierPermissions = {
  can_read: true,
  can_write: true,
  can_delete: false,
  max_customers: -1,
  max_deals: -1,
  max_tasks: -1,
}

const EMPTY_FORM = {
  name: '',
  price: '0',
  stripe_price_id: '',
  is_active: true,
  is_trial: false,
  permissions: { ...DEFAULT_PERMISSIONS },
}

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent text-sm'
const LABEL = 'block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1'

function parsePermissions(raw: any): TierPermissions {
  const p = typeof raw === 'string' ? JSON.parse(raw) : (raw || {})
  return {
    can_read: p.can_read ?? p.canRead ?? true,
    can_write: p.can_write ?? p.canWrite ?? false,
    can_delete: p.can_delete ?? p.canDelete ?? false,
    max_customers: p.max_customers ?? p.maxCustomers ?? -1,
    max_deals: p.max_deals ?? p.maxDeals ?? -1,
    max_tasks: p.max_tasks ?? p.maxTasks ?? -1,
  }
}

function buildPayloadPermissions(p: TierPermissions) {
  return {
    can_read: p.can_read, canRead: p.can_read,
    can_write: p.can_write, canWrite: p.can_write,
    can_delete: p.can_delete, canDelete: p.can_delete,
    max_customers: p.max_customers, maxCustomers: p.max_customers,
    max_deals: p.max_deals, maxDeals: p.max_deals,
    max_tasks: p.max_tasks, maxTasks: p.max_tasks,
  }
}

function limitLabel(v: number) {
  return v === -1 ? 'Unlimited' : v === 0 ? 'None' : String(v)
}

function PlanBadge({ active, trial }: { active: boolean; trial: boolean }) {
  if (trial) return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Trial</span>
  if (active) return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Active</span>
  return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Inactive</span>
}

function PermBadge({ allowed, label }: { allowed: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${allowed ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'}`}>
      <span>{allowed ? '✓' : '✕'}</span> {label}
    </span>
  )
}

const AdminMembershipSettings: React.FC = () => {
  const { isAdmin } = useAuth()
  const [tiers, setTiers] = useState<MembershipTier[]>([])
  const [loading, setLoading] = useState(true)
  const [panelOpen, setPanelOpen] = useState(false)
  const [editingTier, setEditingTier] = useState<MembershipTier | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<MembershipTier | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)

  const token = () => localStorage.getItem('token') || ''
  const authHeaders = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  const fetchTiers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/memberships?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token()}` }
      })
      if (res.ok) {
        const data = await res.json()
        setTiers(data.tiers || [])
      }
    } catch { showToast('Failed to load tiers', 'error') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { if (isAdmin) fetchTiers() }, [isAdmin, fetchTiers])

  const openCreate = () => {
    setEditingTier(null)
    setForm(EMPTY_FORM)
    setPanelOpen(true)
  }

  const openEdit = (tier: MembershipTier) => {
    setEditingTier(tier)
    setForm({
      name: tier.name,
      price: String(tier.price ?? 0),
      stripe_price_id: tier.stripe_price_id || '',
      is_active: tier.is_active ?? true,
      is_trial: tier.is_trial ?? false,
      permissions: parsePermissions(tier.permissions),
    })
    setPanelOpen(true)
  }

  const closePanel = () => {
    setPanelOpen(false)
    setEditingTier(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        price: parseFloat(form.price) || 0,
        stripe_price_id: form.stripe_price_id.trim() || null,
        is_active: form.is_active,
        is_trial: form.is_trial,
        permissions: buildPayloadPermissions(form.permissions),
      }
      const url = editingTier
        ? `${API_BASE_URL}/api/memberships/${editingTier.id}`
        : `${API_BASE_URL}/api/memberships`
      const res = await fetch(url, {
        method: editingTier ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || data.error || 'Save failed')
      showToast(editingTier ? 'Tier updated' : 'Tier created')
      closePanel()
      await fetchTiers()
    } catch (err: any) {
      showToast(err.message || 'Failed to save', 'error')
    } finally { setSaving(false) }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/memberships/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Delete failed')
      showToast('Tier deleted')
      setDeleteTarget(null)
      await fetchTiers()
    } catch (err: any) {
      showToast(err.message || 'Failed to delete', 'error')
    } finally { setDeleting(false) }
  }

  const setP = (key: keyof TierPermissions, value: any) =>
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: value } }))

  const fmt = (n: number) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(n)

  if (!isAdmin) return <div className="p-8 text-center text-gray-500">Access denied</div>

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${toast.type === 'success' ? 'bg-gray-900' : 'bg-red-600'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Membership Tiers</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Manage plans, permissions, and limits. Changes apply immediately on next user request.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Tier
        </button>
      </div>

      {/* Permission system notice */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-300">
        <strong>How permissions work:</strong> Read/Write/Delete control API access. Limits (customers, deals, tasks) set caps — use <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">-1</code> for unlimited, <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">0</code> to block entirely. Admins bypass all subscription checks. The Stripe Price ID links this tier to a Stripe product for automatic tier assignment via <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">subscription.metadata.tier_id</code>.
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white" />
        </div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg mb-4">No tiers yet</p>
          <button onClick={openCreate} className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-lg">
            Create your first tier
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {tiers.map(tier => {
            const perms = parsePermissions(tier.permissions)
            return (
              <div
                key={tier.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border shadow-sm flex flex-col ${
                  tier.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-gray-300 dark:border-gray-600 opacity-60'
                }`}
              >
                <div className="p-5 flex-1">
                  {/* Top row */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{tier.name}</h3>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                        {fmt(tier.price)}
                        <span className="text-sm font-normal text-gray-400">/mo</span>
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <PlanBadge active={tier.is_active} trial={tier.is_trial} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {tier.user_count ?? 0} user{(tier.user_count ?? 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <PermBadge allowed={perms.can_read} label="Read" />
                    <PermBadge allowed={perms.can_write} label="Write" />
                    <PermBadge allowed={perms.can_delete} label="Delete" />
                  </div>

                  {/* Limits */}
                  <div className="space-y-1.5 text-sm border-t border-gray-100 dark:border-gray-700 pt-3">
                    {[
                      { label: 'Customers', val: perms.max_customers },
                      { label: 'Deals', val: perms.max_deals },
                      { label: 'Tasks', val: perms.max_tasks },
                    ].map(({ label, val }) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{label}</span>
                        <span className={`font-medium ${val === -1 ? 'text-green-600 dark:text-green-400' : val === 0 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                          {limitLabel(val)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Stripe Price ID */}
                  {tier.stripe_price_id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-xs text-gray-400 truncate font-mono">
                        <span className="text-gray-500">Stripe: </span>{tier.stripe_price_id}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-5 pb-5 flex gap-2">
                  <button
                    onClick={() => openEdit(tier)}
                    className="flex-1 py-2 text-sm font-semibold bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteTarget(tier)}
                    className="px-4 py-2 text-sm font-semibold bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create / Edit Side Panel */}
      {panelOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="flex-1 bg-black/40" onClick={closePanel} />
          {/* Panel */}
          <div className="w-full max-w-lg bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingTier ? `Edit — ${editingTier.name}` : 'New Membership Tier'}
              </h2>
              <button onClick={closePanel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">×</button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">

              {/* Basic Info */}
              <section className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Basic Info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Name *</label>
                    <input
                      className={INPUT}
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Professional"
                      required
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Monthly Price (CAD) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={INPUT}
                      value={form.price}
                      onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Stripe Price ID</label>
                  <input
                    className={INPUT}
                    value={form.stripe_price_id}
                    onChange={e => setForm(f => ({ ...f, stripe_price_id: e.target.value }))}
                    placeholder="price_1abc… (from Stripe Dashboard)"
                  />
                  <p className="text-xs text-gray-400 mt-1">Used for reference. Webhook tier assignment uses <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">subscription.metadata.tier_id</code>.</p>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-gray-900"
                      checked={form.is_active}
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-gray-900"
                      checked={form.is_trial}
                      onChange={e => setForm(f => ({ ...f, is_trial: e.target.checked, price: e.target.checked ? '0' : f.price }))}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Trial tier</span>
                  </label>
                </div>
                {form.is_trial && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
                    Trial tiers must be $0. Only one trial tier is allowed at a time.
                  </p>
                )}
              </section>

              {/* Access Permissions */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Access Permissions</h3>
                <p className="text-xs text-gray-400">Controls what users on this tier can do in the app.</p>
                {[
                  { key: 'can_read' as const, label: 'Read', desc: 'View customers, deals, tasks' },
                  { key: 'can_write' as const, label: 'Write', desc: 'Create and edit records' },
                  { key: 'can_delete' as const, label: 'Delete', desc: 'Permanently remove records' },
                ].map(({ key, label, desc }) => (
                  <label key={key} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 text-gray-900"
                      checked={form.permissions[key]}
                      onChange={e => setP(key, e.target.checked)}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Can {label}</p>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                  </label>
                ))}
              </section>

              {/* Resource Limits */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Resource Limits</h3>
                <p className="text-xs text-gray-400">Set to <strong>-1</strong> for unlimited, <strong>0</strong> to block access entirely.</p>
                {[
                  { key: 'max_customers' as const, label: 'Max Customers' },
                  { key: 'max_deals' as const, label: 'Max Deals' },
                  { key: 'max_tasks' as const, label: 'Max Tasks' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className={LABEL}>{label}</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="-1"
                        className={`${INPUT} flex-1`}
                        value={form.permissions[key]}
                        onChange={e => setP(key, parseInt(e.target.value) || 0)}
                        placeholder="-1 = unlimited"
                      />
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setP(key, -1)} className="px-2 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 transition-colors">∞</button>
                        <button type="button" onClick={() => setP(key, 0)} className="px-2 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 transition-colors">0</button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {form.permissions[key] === -1 ? '✓ Unlimited' : form.permissions[key] === 0 ? '✕ Blocked' : `Capped at ${form.permissions[key]}`}
                    </p>
                  </div>
                ))}
              </section>

              {/* Actions */}
              <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 pt-4 flex gap-3">
                <button type="button" onClick={closePanel} className="flex-1 py-2.5 text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 text-sm font-semibold bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center justify-center gap-2">
                  {saving && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {saving ? 'Saving…' : editingTier ? 'Update Tier' : 'Create Tier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Tier</h3>
            {(deleteTarget.user_count ?? 0) > 0 ? (
              <>
                <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                  <strong>{deleteTarget.user_count} user{deleteTarget.user_count !== 1 ? 's' : ''}</strong> are currently on the <strong>{deleteTarget.name}</strong> tier. Move them to another tier before deleting.
                </p>
                <button onClick={() => setDeleteTarget(null)} className="w-full py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-lg">
                  Got it
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-semibold rounded-lg">
                    Cancel
                  </button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMembershipSettings
